import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Spinner } from 'react-bootstrap';
import { Chart as ChartJS, CategoryScale, LinearScale, BarElement, ArcElement, Title, Tooltip, Legend } from 'chart.js';
import { Bar, Doughnut } from 'react-chartjs-2';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faMoneyBillWave, faChartLine, faFileInvoiceDollar,
  faSyncAlt, faExclamationTriangle, faCheckCircle,
  faClock, faUser, faTrophy
} from '@fortawesome/free-solid-svg-icons';
import Dexie from 'dexie';
import API_CONFIG2 from '../../API_CONFIG/API_CONFIG2';

ChartJS.register(CategoryScale, LinearScale, BarElement, ArcElement, Title, Tooltip, Legend);

// ============================================================
// CACHE DEXIE (24h) avec fallback périmé
// ============================================================
const db = new Dexie('VentesStatsCacheDB');
db.version(1).stores({ stats: 'key, data, timestamp' });
const CACHE_DURATION = 24 * 60 * 60 * 1000;

async function getCachedStats(key, allowExpired = false) {
  try {
    const entry = await db.stats.get(key);
    if (!entry) return null;
    const age = Date.now() - entry.timestamp;
    if (!allowExpired && age > CACHE_DURATION) return null;
    return entry.data;
  } catch { return null; }
}
async function setCachedStats(key, data) {
  await db.stats.put({ key, data, timestamp: Date.now() });
}
async function getStaleStats(key) {
  return getCachedStats(key, true);
}

// ============================================================
// DÉDOUBLONNAGE DES REQUÊTES EN COURS
// ============================================================
const pendingFetches = new Map();

const API_URL = `${API_CONFIG2.apiUrl}achat/api_ventes_stats.php`;

// ============================================================
// UTILITAIRE FETCH JSON SÉCURISÉ
// ============================================================
const fetchJSON = async (url, options = {}) => {
  const response = await fetch(url, options);
  const text = await response.text();
  if (!response.ok) {
    throw new Error(`HTTP ${response.status} : ${text || 'Erreur inconnue'}`);
  }
  try {
    return JSON.parse(text);
  } catch (parseError) {
    throw new Error(`Réponse JSON invalide : ${text.substring(0, 200) || '(réponse vide)'}`);
  }
};

// ============================================================
// COMPOSANT PRINCIPAL
// ============================================================
const VentesDashboard = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [dataSource, setDataSource] = useState('cache');
  const [toast, setToast] = useState({ show: false, message: '', variant: 'success' });

  const abortControllerRef = useRef(null);
  const toastTimeoutRef = useRef(null);
  const isMounted = useRef(true);

  // ============================================================
  // GESTION DES TOASTS
  // ============================================================
  const showToast = useCallback((message, variant = 'success') => {
    if (toastTimeoutRef.current) clearTimeout(toastTimeoutRef.current);
    setToast({ show: true, message, variant });
    toastTimeoutRef.current = setTimeout(() => {
      if (isMounted.current) setToast({ show: false });
      toastTimeoutRef.current = null;
    }, 5000);
  }, []);

  // ============================================================
  // RÉCUPÉRATION DES STATISTIQUES (avec cache, dédoublonnage, annulation)
  // ============================================================
  const fetchStats = useCallback(async (forceRefresh = false, retryCount = 0) => {
    // Annuler la requête précédente
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    const controller = new AbortController();
    abortControllerRef.current = controller;

    const cacheKey = 'ventes_stats';
    const requestKey = forceRefresh ? `${cacheKey}_force` : cacheKey;

    // Dédoublonnage : si une requête identique est déjà en cours, on attend sa promesse
    if (pendingFetches.has(requestKey) && !forceRefresh) {
      try {
        const result = await pendingFetches.get(requestKey);
        if (isMounted.current && result) {
          setStats(result);
          setDataSource('cache (partagé)');
          setLoading(false);
          setRefreshing(false);
        }
        return;
      } catch {
        pendingFetches.delete(requestKey);
      }
    }

    // Limiter les tentatives (éviter boucle infinie)
    if (retryCount > 5) {
      if (isMounted.current) {
        setLoading(false);
        setRefreshing(false);
        showToast('Erreur : trop de tentatives. Veuillez réessayer plus tard.', 'danger');
      }
      return;
    }

    // Définir l'état de chargement
    if (forceRefresh) setRefreshing(true);
    else setLoading(true);
    setDataSource('');

    const fetchPromise = (async () => {
      try {
        // 1. Cache si non forcé
        if (!forceRefresh) {
          const cached = await getCachedStats(cacheKey);
          if (cached) {
            if (isMounted.current) {
              setStats(cached);
              setDataSource('cache');
              setLoading(false);
              setRefreshing(false);
            }
            return cached;
          }
        }

        // 2. Requête réseau
        const url = `${API_URL}?action=stats`;
        const response = await fetch(url, { signal: controller.signal });

        // 3. Gestion 429
        if (response.status === 429) {
          // Essayer le cache périmé
          const stale = await getStaleStats(cacheKey);
          if (stale) {
            if (isMounted.current) {
              setStats(stale);
              setDataSource('cache (périmé)');
              showToast('429 – Utilisation du cache (données peut-être obsolètes)', 'warning');
              setLoading(false);
              setRefreshing(false);
            }
            return stale;
          }
          // Sinon, backoff exponentiel
          const waitTime = Math.min(1000 * Math.pow(2, retryCount), 30000);
          showToast(`Trop de requêtes. Nouvel essai dans ${waitTime/1000}s`, 'warning');
          await new Promise(resolve => setTimeout(resolve, waitTime));
          return fetchStats(forceRefresh, retryCount + 1);
        }

        // 4. Gestion des autres erreurs HTTP
        if (!response.ok) {
          const stale = await getStaleStats(cacheKey);
          if (stale) {
            if (isMounted.current) {
              setStats(stale);
              setDataSource('cache (périmé)');
              showToast('Erreur réseau – Utilisation du cache', 'warning');
              setLoading(false);
              setRefreshing(false);
            }
            return stale;
          }
          throw new Error(`HTTP ${response.status}`);
        }

        // 5. Lecture JSON
        const data = await response.json();
        if (data.status === 'success') {
          if (isMounted.current) {
            setStats(data.data);
            const source = forceRefresh ? 'live (forcé)' : 'live';
            setDataSource(source);
            await setCachedStats(cacheKey, data.data);
            if (forceRefresh) {
              showToast('Données actualisées', 'success');
            }
            setLoading(false);
            setRefreshing(false);
          }
          return data.data;
        } else {
          throw new Error(data.message || 'Erreur serveur');
        }
      } catch (err) {
        if (err.name === 'AbortError') return null;
        // Dernier recours : cache périmé
        const stale = await getStaleStats(cacheKey);
        if (stale && isMounted.current) {
          setStats(stale);
          setDataSource('cache (périmé)');
          showToast('Erreur – Utilisation du cache', 'warning');
          setLoading(false);
          setRefreshing(false);
          return stale;
        }
        if (isMounted.current) {
          showToast(err.message || 'Erreur de chargement', 'danger');
          setLoading(false);
          setRefreshing(false);
        }
        throw err;
      }
    })();

    // Stocker la promesse dans la Map pour dédoublonner
    pendingFetches.set(requestKey, fetchPromise);
    try {
      const result = await fetchPromise;
      return result;
    } finally {
      pendingFetches.delete(requestKey);
      if (abortControllerRef.current === controller) {
        abortControllerRef.current = null;
      }
    }
  }, [showToast]);

  // ============================================================
  // INITIALISATION ET NETTOYAGE
  // ============================================================
  useEffect(() => {
    isMounted.current = true;
    fetchStats(false);
    return () => {
      isMounted.current = false;
      if (abortControllerRef.current) abortControllerRef.current.abort();
      if (toastTimeoutRef.current) clearTimeout(toastTimeoutRef.current);
      pendingFetches.clear();
    };
  }, [fetchStats]);

  // ============================================================
  // GESTIONNAIRES DE RAFRAÎCHISSEMENT
  // ============================================================
  const handleRefresh = useCallback(() => {
    if (!refreshing && !loading) {
      fetchStats(true);
    }
  }, [fetchStats, refreshing, loading]);

  const handleRefreshForce = useCallback(() => {
    if (!refreshing && !loading) {
      db.stats.clear();
      fetchStats(true);
    }
  }, [fetchStats, refreshing, loading]);

  // ============================================================
  // AFFICHAGE DU CHARGEMENT
  // ============================================================
  if (loading && !stats && !refreshing) {
    return (
      <div className="text-center py-5">
        <Spinner animation="border" variant="primary" />
        <p className="text-secondary mt-2">Chargement des statistiques...</p>
      </div>
    );
  }

  if (!stats) {
    return (
      <div className="text-center py-5 text-danger">
        <FontAwesomeIcon icon={faExclamationTriangle} className="me-2" />
        Aucune donnée disponible.
      </div>
    );
  }

  // ============================================================
  // PRÉPARATION DES DONNÉES POUR LES GRAPHIQUES
  // ============================================================
  const { nbFactures, caTotal, byStatus, evolution, topClients } = stats;

  const doughnutData = {
    labels: byStatus.map(item => item.statut),
    datasets: [{
      data: byStatus.map(item => item.nb),
      backgroundColor: ['#3b82f6', '#10b981', '#ef4444'],
      borderColor: '#0b1428',
      borderWidth: 2,
    }]
  };

  const barEvolutionData = {
    labels: evolution.map(item => item.mois),
    datasets: [
      {
        label: 'Nombre de factures',
        data: evolution.map(item => item.nb),
        backgroundColor: '#3b82f6',
        borderRadius: 4,
        yAxisID: 'y',
      },
      {
        label: 'Montant total ($)',
        data: evolution.map(item => item.montant),
        backgroundColor: '#60a5fa',
        borderRadius: 4,
        yAxisID: 'y1',
      }
    ]
  };

  const barOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        labels: { color: '#e0e7ff', boxWidth: 8, usePointStyle: true, font: { size: 10 } }
      }
    },
    scales: {
      y: {
        type: 'linear',
        display: true,
        position: 'left',
        grid: { color: 'rgba(255,255,255,0.05)' },
        ticks: { color: '#94a3b8' }
      },
      y1: {
        type: 'linear',
        display: true,
        position: 'right',
        grid: { drawOnChartArea: false },
        ticks: { color: '#94a3b8' }
      },
      x: {
        grid: { display: false },
        ticks: { color: '#94a3b8' }
      }
    }
  };

  const doughnutOptions = {
    responsive: true,
    maintainAspectRatio: false,
    cutout: '70%',
    plugins: {
      legend: {
        labels: { color: '#e0e7ff', boxWidth: 8, usePointStyle: true, font: { size: 10 } }
      }
    }
  };

  // ============================================================
  // RENDU PRINCIPAL (avec styles responsifs améliorés)
  // ============================================================
  return (
    <>
      <style>{`
        :root {
          --accent-blue: #3b82f6;
          --accent-dark-blue: #1d4ed8;
          --accent-blue-glow: rgba(59, 130, 246, 0.35);
          --bg-primary: #0b1428;
          --bg-gradient: radial-gradient(circle at 20% 30%, #132044, #0a0f1f);
          --card-bg: rgba(20, 40, 80, 0.5);
          --card-border: rgba(59, 130, 246, 0.15);
          --text-primary: #e0e7ff;
          --text-secondary: #94a3b8;
          --text-muted: #64748b;
          --radius: 18px;
          --shadow: 0 12px 40px rgba(0, 0, 0, 0.5);
        }

        .dashboard-ventes {
          position: relative;
          z-index: 1;
          background: var(--bg-gradient);
          min-height: 100vh;
          padding: clamp(0.75rem, 2vw, 2rem);
          color: var(--text-primary);
          font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
        }

        .glass-card {
          background: var(--card-bg);
          backdrop-filter: blur(16px);
          -webkit-backdrop-filter: blur(16px);
          border: 1px solid var(--card-border);
          border-radius: var(--radius);
          padding: clamp(1rem, 1.5vw, 1.8rem);
          box-shadow: var(--shadow);
          transition: all 0.25s ease;
          margin-bottom: 24px;
        }
        .glass-card:hover {
          border-color: rgba(59, 130, 246, 0.3);
          box-shadow: 0 12px 40px rgba(59, 130, 246, 0.08);
          transform: translateY(-2px);
        }

        .badge-source {
          font-size: clamp(0.6rem, 0.9vw, 0.75rem);
          padding: 4px 12px;
          border-radius: 40px;
          background: rgba(59,130,246,0.15);
          color: var(--accent-blue);
          border: 1px solid rgba(59,130,246,0.12);
          white-space: nowrap;
          font-weight: 500;
          display: inline-block;
        }
        .badge-source.live {
          background: rgba(46,204,113,0.15);
          color: #2ecc71;
          border-color: rgba(46,204,113,0.12);
        }
        .badge-source.warning {
          background: rgba(245,158,11,0.15);
          color: #f59e0b;
          border-color: rgba(245,158,11,0.12);
        }

        .monetra-btn {
          padding: 8px 16px;
          border-radius: 30px;
          border: 1px solid rgba(255,255,255,0.06);
          background: rgba(255,255,255,0.04);
          color: var(--text-primary);
          transition: all 0.25s ease;
          display: inline-flex;
          align-items: center;
          gap: 6px;
          font-weight: 500;
          cursor: pointer;
          font-size: clamp(0.75rem, 0.9vw, 0.9rem);
          white-space: nowrap;
          min-height: 44px;
          touch-action: manipulation;
        }
        .monetra-btn:hover:not(:disabled) {
          background: rgba(255,255,255,0.08);
          transform: translateY(-2px);
          box-shadow: 0 6px 16px rgba(0,0,0,0.2);
        }
        .monetra-btn:disabled {
          opacity: 0.4;
          pointer-events: none;
        }
        .monetra-btn.primary {
          background: var(--accent-blue);
          color: #fff;
          border: none;
          box-shadow: 0 6px 20px var(--accent-blue-glow);
        }
        .monetra-btn.primary:hover:not(:disabled) {
          background: var(--accent-dark-blue);
          box-shadow: 0 8px 28px rgba(59, 130, 246, 0.4);
        }
        .monetra-btn.outline {
          border-color: rgba(255,255,255,0.08);
          color: var(--text-secondary);
        }
        .monetra-btn.outline:hover:not(:disabled) {
          background: rgba(255,255,255,0.05);
        }

        .kpi-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(clamp(150px, 22vw, 220px), 1fr));
          gap: clamp(12px, 2vw, 20px);
          margin-bottom: 20px;
        }
        .kpi-item {
          background: rgba(255,255,255,0.02);
          border: 1px solid rgba(255,255,255,0.04);
          border-radius: 20px;
          padding: 1.2rem 1rem;
          text-align: center;
          transition: all 0.3s ease;
        }
        .kpi-item:hover {
          transform: translateY(-3px);
          border-color: rgba(59, 130, 246, 0.2);
          box-shadow: 0 0 30px rgba(59, 130, 246, 0.08);
        }
        .kpi-value {
          font-size: clamp(1.8rem, 3.5vw, 2.2rem);
          font-weight: 700;
          margin-top: 6px;
          color: var(--accent-blue);
          font-variant-numeric: tabular-nums;
        }
        .kpi-label {
          color: var(--text-secondary);
          font-size: clamp(0.7rem, 0.9vw, 0.8rem);
          font-weight: 500;
          text-transform: uppercase;
          letter-spacing: 0.5px;
        }

        .charts-grid {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: clamp(12px, 2vw, 20px);
        }
        .chart-box {
          height: clamp(180px, 30vh, 220px);
          position: relative;
        }

        .list-item {
          display: flex;
          justify-content: space-between;
          padding: 8px 0;
          border-bottom: 1px solid rgba(255,255,255,0.04);
          font-size: clamp(0.85rem, 1vw, 1rem);
        }
        .list-item:last-child {
          border-bottom: none;
        }
        .list-item .rank {
          font-weight: 700;
          color: var(--text-secondary);
          margin-right: 10px;
        }

        .toast-container {
          position: fixed;
          top: clamp(12px, 2vh, 24px);
          right: clamp(12px, 2vw, 24px);
          z-index: 9999;
          display: flex;
          flex-direction: column;
          gap: 12px;
          max-width: 440px;
        }
        .toast-item {
          background: rgba(10, 20, 45, 0.8);
          backdrop-filter: blur(24px);
          -webkit-backdrop-filter: blur(24px);
          border: 1px solid rgba(59, 130, 246, 0.2);
          border-radius: var(--radius);
          padding: 12px 18px;
          color: var(--text-primary);
          display: flex;
          align-items: center;
          gap: 14px;
          box-shadow: 0 8px 32px rgba(0,0,0,0.4);
          animation: slideIn 0.35s ease;
          font-size: clamp(0.85rem, 1.2vw, 1rem);
        }
        .toast-item.success { border-left: 4px solid var(--accent-blue); }
        .toast-item.warning { border-left: 4px solid #f59e0b; }
        .toast-item.danger { border-left: 4px solid #ef4444; }
        @keyframes slideIn {
          from { opacity: 0; transform: translateX(40px) scale(0.96); }
          to { opacity: 1; transform: translateX(0) scale(1); }
        }
        .btn-close-custom {
          background: transparent;
          border: none;
          color: var(--text-secondary);
          opacity: 0.5;
          cursor: pointer;
          font-size: 1.2rem;
          padding: 0 4px;
          margin-left: auto;
          transition: color 0.2s;
        }
        .btn-close-custom:hover {
          opacity: 1;
        }

        .text-secondary { color: var(--text-secondary); }
        .text-muted { color: var(--text-muted); }
        .text-success { color: var(--accent-blue); }
        .text-danger { color: #ef4444; }
        .fw-bold { font-weight: 600; }
        .small { font-size: clamp(0.65rem, 0.8vw, 0.85rem); }
        .m-0 { margin: 0; }
        .mb-4 { margin-bottom: clamp(1rem, 2vw, 1.5rem); }
        .me-2 { margin-right: 0.5rem; }
        .gap-2 { gap: 0.5rem; }
        .gap-3 { gap: 1rem; }
        .d-flex { display: flex; }
        .flex-wrap { flex-wrap: wrap; }
        .align-items-center { align-items: center; }
        .justify-content-between { justify-content: space-between; }
        .text-center { text-align: center; }
        .py-5 { padding-top: 3rem; padding-bottom: 3rem; }

        @media (max-width: 768px) {
          .dashboard-ventes { padding: 12px; }
          .charts-grid { grid-template-columns: 1fr; }
          .kpi-grid { grid-template-columns: 1fr 1fr; }
          .monetra-btn { font-size: 0.75rem; padding: 6px 12px; min-height: 38px; }
          .glass-card { padding: 1rem; }
        }
        @media (max-width: 480px) {
          .kpi-grid { grid-template-columns: 1fr; }
          .kpi-value { font-size: 1.6rem; }
          .chart-box { height: 160px; }
          .monetra-btn { font-size: 0.7rem; padding: 4px 10px; min-height: 34px; }
        }
      `}</style>

      <div className="dashboard-ventes">
        {/* En-tête avec badge source et boutons */}
        <div className="d-flex justify-content-between align-items-center mb-4 flex-wrap gap-3">
          <div>
            <h1 className="fw-bold mb-1" style={{ fontSize: 'clamp(1.6rem, 4vw, 2.2rem)', color: 'var(--text-primary)' }}>
              Tableau de bord des ventes
            </h1>
            <p className="text-secondary m-0" style={{ fontSize: 'clamp(0.75rem, 1.2vw, 0.95rem)' }}>
              Analyse des factures et du chiffre d'affaires.
            </p>
          </div>
          <div className="d-flex align-items-center gap-2 flex-wrap">
            <span className={`badge-source ${dataSource === 'live' || dataSource === 'live (forcé)' ? 'live' : dataSource === 'cache (périmé)' ? 'warning' : ''}`}>
              {dataSource === 'cache' ? '📦 Cache' :
               dataSource === 'cache (périmé)' ? '⚠️ Cache périmé' :
               dataSource === 'cache (partagé)' ? '🔄 Partage' :
               dataSource === 'live' ? '🔄 Live' :
               dataSource === 'live (forcé)' ? '🔄 Forcé' : '📦 Cache'}
            </span>
            <button
              className="monetra-btn outline"
              onClick={handleRefresh}
              disabled={refreshing || loading}
            >
              <FontAwesomeIcon icon={faSyncAlt} spin={refreshing} /> Actualiser
            </button>
            <button
              className="monetra-btn outline"
              onClick={handleRefreshForce}
              disabled={refreshing || loading}
            >
              <FontAwesomeIcon icon={faSyncAlt} spin={refreshing} style={{ color: '#ef4444' }} /> Forcer
            </button>
          </div>
        </div>

        {/* KPIs */}
        <div className="glass-card">
          <div className="kpi-grid">
            <div className="kpi-item">
              <FontAwesomeIcon icon={faFileInvoiceDollar} className="text-secondary mb-2" style={{ fontSize: 'clamp(1.2rem, 2vw, 1.5rem)' }} />
              <div className="kpi-label">Nombre de factures</div>
              <div className="kpi-value">{nbFactures}</div>
            </div>
            <div className="kpi-item">
              <FontAwesomeIcon icon={faMoneyBillWave} className="text-secondary mb-2" style={{ fontSize: 'clamp(1.2rem, 2vw, 1.5rem)' }} />
              <div className="kpi-label">Chiffre d'affaires total</div>
              <div className="kpi-value">{caTotal.toFixed(2)} USD</div>
            </div>
            <div className="kpi-item">
              <FontAwesomeIcon icon={faChartLine} className="text-secondary mb-2" style={{ fontSize: 'clamp(1.2rem, 2vw, 1.5rem)' }} />
              <div className="kpi-label">Statuts différents</div>
              <div className="kpi-value">{byStatus.length}</div>
            </div>
            <div className="kpi-item">
              <FontAwesomeIcon icon={faUser} className="text-secondary mb-2" style={{ fontSize: 'clamp(1.2rem, 2vw, 1.5rem)' }} />
              <div className="kpi-label">Top clients</div>
              <div className="kpi-value">{topClients.length}</div>
            </div>
          </div>
        </div>

        {/* Graphiques */}
        <div className="glass-card">
          <div className="charts-grid">
            <div className="chart-box">
              <div className="text-secondary mb-2" style={{ fontSize: 'clamp(0.8rem, 1.1vw, 0.9rem)', fontWeight: 500 }}>
                Répartition par statut
              </div>
              <Doughnut data={doughnutData} options={doughnutOptions} />
            </div>
            <div className="chart-box">
              <div className="text-secondary mb-2" style={{ fontSize: 'clamp(0.8rem, 1.1vw, 0.9rem)', fontWeight: 500 }}>
                Évolution (12 mois)
              </div>
              <Bar data={barEvolutionData} options={barOptions} />
            </div>
          </div>
        </div>

        {/* Top clients */}
        <div className="glass-card">
          <h5 className="mb-3 fw-bold" style={{ color: 'var(--accent-blue)', fontSize: 'clamp(1rem, 1.4vw, 1.2rem)' }}>
            <FontAwesomeIcon icon={faTrophy} className="me-2" />
            Top 5 clients (par montant)
          </h5>
          {topClients.length === 0 ? (
            <p className="text-secondary">Aucun client enregistré.</p>
          ) : (
            topClients.map((client, index) => (
              <div key={index} className="list-item">
                <span>
                  <span className="rank">#{index + 1}</span>
                  {client.nom}
                </span>
                <span className="fw-bold" style={{ color: 'var(--accent-blue)' }}>
                  {client.totalAchats.toFixed(2)} $
                </span>
              </div>
            ))
          )}
        </div>
      </div>

      {/* Toast */}
      {toast.show && (
        <div className="toast-container">
          <div className={`toast-item ${toast.variant}`}>
            <FontAwesomeIcon
              icon={toast.variant === 'success' ? faCheckCircle : faExclamationTriangle}
              className={toast.variant === 'success' ? 'text-success' : 'text-danger'}
            />
            <span>{toast.message}</span>
            <button className="btn-close-custom" onClick={() => setToast({ ...toast, show: false })}>✕</button>
          </div>
        </div>
      )}
    </>
  );
};

export default VentesDashboard;